import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Trash2, PackagePlus } from 'lucide-react';
import apiClient from '../api/client';
import { useToast } from '../components/ui/Toast';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { inputClass } from '../components/ui/Input';
import { ArticleImage } from '../components/ui/ArticleImage';
import { formatEUR } from '../lib/format';

/**
 * Purchasing → Replenishment.
 *
 * Buying a supplier's external stock onto OUR shelf. Deliberately separate from the sale flow: no
 * customer is attached, nothing is allocated, and on reception the quantity simply becomes internal
 * stock — no re-keying a delivery note into a stock import, which is the double handling the manual
 * (non-API) supplier path forces.
 *
 * This screen is one of only two places allowed to spend the supplier API's rate limit (the other is
 * the order/sale flow), which is why the catalogue and product-management screens no longer look up
 * external stock at all.
 *
 * One line per part, both figures on it — same as the order page, and for the same reason. A buyer
 * topping up the shelf needs to see what we hold NEXT TO what the supplier holds; deciding how many
 * to order is precisely a comparison of the two. This screen used to drop every part we already
 * carry ("use Supplier POs to restock it"), which left it able to buy only parts we DON'T stock —
 * backwards for a replenishment desk, whose ordinary job is topping up what we do.
 */
interface SupplierHit {
  id: string;
  sku: string;
  name: string;
  brand: string | null;
  stock: number;
  imageUrl: string | null;
  brandImageUrl: string | null;
  providedBy: string | null;
  source: 'CATALOG' | 'SUPPLIER';
  supplier?: {
    supplierCode?: string;
    supplierName?: string;
    supplierArticleNo: string;
    availableQty: number | null;
    leadTimeDays: number | null;
    unitPriceHT?: number;
    indicativeSellPriceHT: number | null;
  };
}

interface Availability {
  productId: string;
  internalStock: number;
  supplierAvailability: {
    supplierCode: string;
    supplierName: string;
    supplierArticleNo: string | null;
    status: 'OK' | 'UNAVAILABLE' | 'CIRCUIT_OPEN' | 'TIMEOUT' | 'MANUAL_ENTRY' | 'API_ERROR';
    availableQty: number | null;
    leadDays: number | null;
    manualEntry: boolean;
    bestPrice: boolean;
    unitPriceHT?: number | null;
  }[];
}

/** A supplier we can actually raise a PO against: named, with an article number and a cost. */
interface Offer {
  supplierCode: string;
  supplierName: string;
  supplierArticleNo: string;
  availableQty: number | null;
  leadTimeDays: number | null;
  unitPriceHT: number;
}

/** One part, whether or not we carry it. `internalStock === null` = not in our catalogue. */
interface Row {
  key: string;
  productId: string | null;
  reference: string;
  name: string;
  brand: string | null;
  imageUrl: string | null;
  brandImageUrl: string | null;
  providedBy: string | null;
  internalStock: number | null;
  offers: Offer[];
}

interface BasketLine {
  productId: string | null;
  reference: string;
  name: string;
  brand: string | null;
  imageUrl: string | null;
  brandImageUrl: string | null;
  supplierCode: string;
  supplierName: string;
  supplierArticleNo: string;
  unitPriceHT: number;
  availableQty: number | null;
  leadTimeDays: number | null;
  quantity: number;
}

/** Buy-ready first (in stock, then cheapest) — what a buyer would pick if we made them scan. */
const byBuyability = (a: Offer, b: Offer) =>
  Number((b.availableQty ?? 0) > 0) - Number((a.availableQty ?? 0) > 0) ||
  a.unitPriceHT - b.unitPriceHT ||
  (a.leadTimeDays ?? Infinity) - (b.leadTimeDays ?? Infinity);

export default function Replenishment() {
  const { t } = useTranslation();
  const { push, showApiError } = useToast();
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<Row[] | null>(null);
  const [searching, setSearching] = useState(false);
  // The supplier fan-out for parts we already carry lands after the rows do, so the table can
  // render our stock immediately instead of waiting on every supplier to answer.
  const [enriching, setEnriching] = useState(false);
  const [chosen, setChosen] = useState<Record<string, string>>({});
  const [basket, setBasket] = useState<BasketLine[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const search = async () => {
    const text = q.trim();
    if (!text) return;
    setSearching(true);
    setChosen({});
    try {
      const { data } = await apiClient.get<SupplierHit[]>(
        `/catalog/search?q=${encodeURIComponent(text)}&mode=ALL&supplier=1`
      );
      // Every row can carry a quote for the searched reference — a supplier row by definition, a
      // catalogue row because the search attaches the live offer for the same part. Any FURTHER
      // suppliers (ones bridged to the product but quoting a different article number) arrive with
      // the availability batch below.
      const base: Row[] = data.map((h) => ({
        key: h.id,
        productId: h.source === 'CATALOG' ? h.id : null,
        reference: h.sku,
        name: h.name,
        brand: h.brand,
        imageUrl: h.imageUrl,
        brandImageUrl: h.brandImageUrl,
        providedBy: h.providedBy,
        internalStock: h.source === 'CATALOG' ? h.stock : null,
        offers:
          h.supplier?.supplierCode && h.supplier.unitPriceHT != null
            ? [
                {
                  supplierCode: h.supplier.supplierCode,
                  supplierName: h.supplier.supplierName ?? h.supplier.supplierCode,
                  supplierArticleNo: h.supplier.supplierArticleNo,
                  availableQty: h.supplier.availableQty,
                  leadTimeDays: h.supplier.leadTimeDays,
                  unitPriceHT: h.supplier.unitPriceHT,
                },
              ]
            : [],
      }));
      setRows(base);
      void enrich(base);
    } catch (err) {
      showApiError(err);
    } finally {
      setSearching(false);
    }
  };

  /**
   * Hang live supplier quotes off the parts we already carry. ONE batch call for the whole page —
   * /catalog/availability makes a single supplier fan-out for the id set, so a full result page
   * costs one round-trip rather than one per row. Spending the quota is the point of this screen.
   */
  const enrich = async (base: Row[]) => {
    const ids = base.map((r) => r.productId).filter((id): id is string => !!id);
    if (ids.length === 0) return;
    setEnriching(true);
    try {
      const { data } = await apiClient.get<Availability[]>(
        `/catalog/availability?ids=${encodeURIComponent(ids.join(','))}`
      );
      const byId = new Map(data.map((a) => [a.productId, a]));
      setRows((cur) =>
        (cur ?? []).map((r) => {
          const a = r.productId ? byId.get(r.productId) : undefined;
          if (!a) return r;
          // Only offers we could actually place: a manual supplier has no article number to
          // order against, and without a cost there is no PO line to write.
          const extra = a.supplierAvailability
            .filter((s) => !s.manualEntry && s.supplierArticleNo && s.unitPriceHT != null)
            .map((s) => ({
              supplierCode: s.supplierCode,
              supplierName: s.supplierName,
              supplierArticleNo: s.supplierArticleNo!,
              availableQty: s.availableQty,
              leadTimeDays: s.leadDays,
              unitPriceHT: s.unitPriceHT!,
            }));
          // ADD to what the search already quoted, one offer per supplier. The search quote wins a
          // tie: it answered the reference the buyer actually typed.
          const seen = new Set(r.offers.map((o) => o.supplierCode));
          return {
            ...r,
            internalStock: a.internalStock,
            offers: [...r.offers, ...extra.filter((o) => !seen.has(o.supplierCode))].sort(
              byBuyability
            ),
          };
        })
      );
    } catch (err) {
      showApiError(err);
    } finally {
      setEnriching(false);
    }
  };

  const offerFor = (r: Row): Offer | undefined =>
    r.offers.find((o) => o.supplierCode === chosen[r.key]) ?? r.offers[0];

  const add = (r: Row) => {
    const o = offerFor(r);
    if (!o) {
      push({
        tone: 'warning',
        message: t('replenishment.noQuoteToast'),
      });
      return;
    }
    setBasket((cur) => {
      const i = cur.findIndex(
        (l) => l.supplierCode === o.supplierCode && l.supplierArticleNo === o.supplierArticleNo
      );
      if (i >= 0) {
        const next = [...cur];
        next[i] = { ...next[i], quantity: next[i].quantity + 1 };
        return next;
      }
      return [
        ...cur,
        {
          productId: r.productId,
          reference: r.reference,
          name: r.name,
          brand: r.brand,
          imageUrl: r.imageUrl,
          brandImageUrl: r.brandImageUrl,
          supplierCode: o.supplierCode,
          supplierName: o.supplierName,
          supplierArticleNo: o.supplierArticleNo,
          unitPriceHT: o.unitPriceHT,
          availableQty: o.availableQty,
          leadTimeDays: o.leadTimeDays,
          quantity: 1,
        },
      ];
    });
  };

  const lineKey = (l: BasketLine) => `${l.supplierCode}:${l.supplierArticleNo}`;
  const setQty = (key: string, qty: number) =>
    setBasket((cur) =>
      cur.map((l) => (lineKey(l) === key ? { ...l, quantity: Math.max(1, qty) } : l))
    );
  const remove = (key: string) => setBasket((cur) => cur.filter((l) => lineKey(l) !== key));

  // One PO per supplier: a purchase order is a contract with one supplier.
  const bySupplier = basket.reduce<Record<string, BasketLine[]>>((acc, l) => {
    (acc[l.supplierCode] ??= []).push(l);
    return acc;
  }, {});
  const totalHT = basket.reduce((s, l) => s + l.unitPriceHT * l.quantity, 0);

  const submit = async () => {
    setSubmitting(true);
    try {
      for (const [supplierCode, lines] of Object.entries(bySupplier)) {
        await apiClient.post('/purchasing/replenishment', {
          supplierCode,
          lines: lines.map((l) => ({
            reference: l.reference,
            // Present for a part we already carry, so the PO tops up THAT row rather than
            // re-deriving the product from its reference.
            ...(l.productId ? { productId: l.productId } : {}),
            supplierArticleNo: l.supplierArticleNo,
            quantity: l.quantity,
            unitPriceHT: l.unitPriceHT,
          })),
        });
      }
      push({
        tone: 'success',
        title: t('replenishment.raisedTitle'),
        message: t('replenishment.raisedMessage'),
      });
      setBasket([]);
    } catch (err) {
      showApiError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const orderable = (rows ?? []).filter((r) => r.offers.length > 0).length;

  return (
    <div>
      <PageHeader title={t('replenishment.title')} description={t('replenishment.description')} />

      <div className="mb-4 flex items-center gap-2 rounded-lg border bg-card p-3 shadow-sm">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void search()}
            placeholder={t('replenishment.searchPlaceholder')}
            className={inputClass + ' pl-8'}
          />
        </div>
        <Button size="sm" onClick={() => void search()} disabled={searching}>
          <Search className="h-4 w-4" />
          {searching ? t('common.searching') : t('replenishment.searchButton')}
        </Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_380px]">
        <div className="rounded-lg border bg-card">
          <div className="flex items-center justify-between border-b px-3 py-2 text-sm font-semibold">
            <span>
              {t('replenishment.compareHeading')}
              {rows ? t('replenishment.orderable', { count: orderable }) : ''}
            </span>
            {enriching && (
              <span className="text-xs font-normal text-muted-foreground">
                {t('replenishment.asking')}
              </span>
            )}
          </div>
          {!rows ? (
            <p className="p-6 text-center text-sm text-muted-foreground">
              {t('replenishment.emptyPrompt')}
            </p>
          ) : rows.length === 0 ? (
            <p className="p-6 text-center text-sm text-muted-foreground">
              {t('replenishment.notFound')}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-muted-foreground">
                  <tr className="[&>th]:px-3 [&>th]:py-2 [&>th]:text-left">
                    <th />
                    <th>{t('replenishment.columns.reference')}</th>
                    <th>{t('common.name')}</th>
                    <th>{t('replenishment.columns.ourStock')}</th>
                    <th>{t('replenishment.columns.buyFrom')}</th>
                    <th>{t('replenishment.columns.supplierStock')}</th>
                    <th>{t('replenishment.columns.lead')}</th>
                    <th className="text-right">{t('replenishment.columns.cost')}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const o = offerFor(r);
                    return (
                      <tr key={r.key} className="border-t">
                        <td className="px-3 py-2">
                          <ArticleImage
                            src={r.imageUrl}
                            brandSrc={r.brandImageUrl}
                            alt={r.brand ?? r.name}
                            size={36}
                          />
                        </td>
                        <td className="px-3 py-2 font-mono">{r.reference}</td>
                        <td className="px-3 py-2">
                          <div>{r.name}</div>
                          {r.providedBy && (
                            <div className="text-[11px] text-muted-foreground">
                              {t('replenishment.via', { supplier: r.providedBy })}
                            </div>
                          )}
                        </td>
                        {/* Our shelf. A part not in the catalogue has no stock figure at all — a
                            different thing from holding zero, and worth showing as such. */}
                        <td className="px-3 py-2">
                          {r.internalStock == null ? (
                            <span
                              className="text-xs text-muted-foreground"
                              title={t('replenishment.notCarriedTitle')}
                            >
                              {t('replenishment.notCarried')}
                            </span>
                          ) : (
                            <b>{r.internalStock}</b>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {r.offers.length > 1 ? (
                            <select
                              aria-label={t('replenishment.supplierFor', {
                                reference: r.reference,
                              })}
                              value={o?.supplierCode ?? ''}
                              onChange={(e) =>
                                setChosen((c) => ({ ...c, [r.key]: e.target.value }))
                              }
                              className="h-7 rounded-md border border-input bg-background px-1 text-xs"
                            >
                              {r.offers.map((x) => (
                                <option key={x.supplierCode} value={x.supplierCode}>
                                  {x.supplierName} — {formatEUR(x.unitPriceHT)}
                                </option>
                              ))}
                            </select>
                          ) : (
                            (o?.supplierName ?? (
                              <span className="text-xs text-muted-foreground">
                                {enriching ? '…' : t('replenishment.noQuote')}
                              </span>
                            ))
                          )}
                        </td>
                        <td className="px-3 py-2">{o?.availableQty ?? '—'}</td>
                        <td className="px-3 py-2">
                          {o?.leadTimeDays == null
                            ? '—'
                            : o.leadTimeDays === 0
                              ? t('replenishment.today')
                              : t('replenishment.days', { count: o.leadTimeDays })}
                        </td>
                        <td className="px-3 py-2 text-right font-mono">
                          {o ? `${formatEUR(o.unitPriceHT)}` : '—'}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            onClick={() => add(r)}
                            disabled={!o}
                            className="inline-flex h-7 items-center gap-1 rounded-md border border-input bg-background px-2 text-xs transition-colors hover:bg-accent hover:text-accent-foreground disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            <PackagePlus className="h-3.5 w-3.5" /> {t('replenishment.add')}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <aside className="h-fit rounded-lg border bg-card">
          <div className="border-b px-3 py-2 text-sm font-semibold">
            {t('replenishment.basketTitle')}
            {basket.length > 0 && t('replenishment.basketCount', { count: basket.length })}
          </div>
          {basket.length === 0 ? (
            <p className="p-6 text-center text-sm text-muted-foreground">
              {t('replenishment.basketEmpty')}
            </p>
          ) : (
            <>
              <ul className="divide-y">
                {basket.map((l) => (
                  <li key={lineKey(l)} className="flex items-start gap-2 p-3">
                    <ArticleImage
                      src={l.imageUrl}
                      brandSrc={l.brandImageUrl}
                      alt={l.brand ?? l.name}
                      size={32}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-mono text-xs">{l.reference}</div>
                      <div className="truncate text-xs text-muted-foreground">{l.name}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {t('replenishment.perUnit', {
                          supplier: l.supplierName,
                          price: formatEUR(l.unitPriceHT),
                        })}
                        {!l.productId && t('replenishment.newProduct')}
                      </div>
                    </div>
                    <input
                      type="number"
                      min={1}
                      value={l.quantity}
                      onChange={(e) => setQty(lineKey(l), Number(e.target.value))}
                      className="h-7 w-14 rounded-md border border-input bg-background px-1 text-center text-xs"
                    />
                    <button
                      onClick={() => remove(lineKey(l))}
                      aria-label={t('replenishment.removeLine', { reference: l.reference })}
                      className="p-1 text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
              <div className="space-y-2 border-t p-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('common.totalHT')}</span>
                  <span className="font-mono font-semibold">{formatEUR(totalHT)}</span>
                </div>
                {Object.keys(bySupplier).length > 1 && (
                  <p className="text-xs text-muted-foreground">
                    {t('replenishment.multiSupplier', {
                      count: Object.keys(bySupplier).length,
                    })}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">{t('replenishment.createdNote')}</p>
                <Button className="w-full" onClick={() => void submit()} disabled={submitting}>
                  {submitting ? t('replenishment.raising') : t('replenishment.raise')}
                </Button>
              </div>
            </>
          )}
        </aside>
      </div>
    </div>
  );
}
